import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  BarChart3,
  RefreshCw,
  Search,
  TrendingUp,
} from "lucide-react";
import {
  warehouseMonitoringService,
  WarehouseAnalyticsGroupBy,
  WarehouseAnalyticsResult,
} from "../../../services/WarehouseMonitoringService";
import { WarehouseMultiSelectFilter } from "./WarehouseMultiSelectFilter";
import { useWarehouseFilterOptions } from "../../../hooks/useWarehouseFilterOptions";

function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function defaultFromDate(groupBy: WarehouseAnalyticsGroupBy): string {
  const date = new Date();
  if (groupBy === "month") {
    date.setFullYear(date.getFullYear() - 1);
  } else {
    date.setDate(date.getDate() - 30);
  }
  return toDateInputValue(date);
}

function formatPeriodLabel(period: string, groupBy: WarehouseAnalyticsGroupBy): string {
  if (groupBy === "month") {
    const [year, month] = period.split("-");
    const d = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
  }

  const [year, month, day] = period.split("-");
  const d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

function formatCurrency(value: number, currencyCode: string | null): string {
  const currency = currencyCode || "EUR";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toLocaleString("en-US")} ${currency}`;
  }
}

function formatNumber(value: number, digits = 0): string {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

interface BarChartProps {
  title: string;
  data: Array<{ label: string; value: number }>;
  valueFormatter: (value: number) => string;
  colorClass?: string;
}

function SimpleBarChart({
  title,
  data,
  valueFormatter,
  colorClass = "bg-indigo-500",
}: BarChartProps) {
  const maxValue = Math.max(...data.map((item) => item.value), 1);

  if (data.length === 0) {
    return (
      <div className="bg-white p-4 border border-gray-200 rounded-md">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
        <div className="text-sm text-gray-500 py-8 text-center">No data</div>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 border border-gray-200 rounded-md">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="flex items-end gap-2 h-52 overflow-x-auto pb-2">
        {data.map((item) => (
          <div
            key={item.label}
            className="flex flex-col items-center min-w-[48px] flex-1"
            title={`${item.label}: ${valueFormatter(item.value)}`}
          >
            <div className="w-full flex items-end justify-center h-40">
              <div
                className={`w-full max-w-[40px] rounded-t ${colorClass}`}
                style={{
                  height: `${Math.max(4, (item.value / maxValue) * 100)}%`,
                }}
              />
            </div>
            <span className="mt-2 text-[10px] text-gray-500 text-center leading-tight">
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface BreakdownTableProps {
  title: string;
  rows: Array<{ key: string; count: number; revenue: number }>;
  currencyCode: string | null;
  resolveLabel?: (key: string) => string;
}

function BreakdownTable({ title, rows, currencyCode, resolveLabel }: BreakdownTableProps) {
  return (
    <div className="bg-white p-4 border border-gray-200 rounded-md">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {rows.length === 0 ? (
        <div className="text-sm text-gray-500">No data</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="table-header">Label</th>
                <th className="table-header">Reservations</th>
                <th className="table-header">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.key}>
                  <td className="table-cell font-medium" title={row.key}>
                    {resolveLabel ? resolveLabel(row.key) : row.key}
                  </td>
                  <td className="table-cell">{formatNumber(row.count)}</td>
                  <td className="table-cell">
                    {formatCurrency(row.revenue, currencyCode)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function WarehouseAnalytics() {
  const {
    hotelOptions,
    selectedHotelCodes,
    setSelectedHotelCodes,
    hotelCodeFilter,
    getHotelDisplayName,
    ensureHotelNames,
    statsError,
  } = useWarehouseFilterOptions();
  const [analytics, setAnalytics] = useState<WarehouseAnalyticsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [groupBy, setGroupBy] = useState<WarehouseAnalyticsGroupBy>("day");
  const [filters, setFilters] = useState({
    from: defaultFromDate("day"),
    to: toDateInputValue(new Date()),
  });

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const fromIso = filters.from
        ? new Date(`${filters.from}T00:00:00.000Z`).toISOString()
        : undefined;
      const toIso = filters.to
        ? new Date(`${filters.to}T23:59:59.999Z`).toISOString()
        : undefined;

      const result = await warehouseMonitoringService.getAnalytics({
        hotelCode: hotelCodeFilter,
        from: fromIso,
        to: toIso,
        groupBy,
      });

      setAnalytics(result);
      await ensureHotelNames((result.byHotel ?? []).map(row => row.key));
    } catch (err) {
      console.error("Error loading warehouse analytics:", err);
      setError(
        err instanceof Error ? err.message : "Unable to load statistics"
      );
    } finally {
      setLoading(false);
    }
  }, [ensureHotelNames, filters.from, filters.to, groupBy, hotelCodeFilter]);

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGroupByChange = (nextGroupBy: WarehouseAnalyticsGroupBy) => {
    setGroupBy(nextGroupBy);
    setFilters((prev) => ({
      ...prev,
      from: defaultFromDate(nextGroupBy),
    }));
  };

  const reservationChartData = useMemo(
    () =>
      (analytics?.byPeriod ?? []).map((row) => ({
        label: formatPeriodLabel(row.period, analytics?.filters.groupBy ?? groupBy),
        value: row.reservations,
      })),
    [analytics, groupBy]
  );

  const revenueChartData = useMemo(
    () =>
      (analytics?.byPeriod ?? []).map((row) => ({
        label: formatPeriodLabel(row.period, analytics?.filters.groupBy ?? groupBy),
        value: row.revenue,
      })),
    [analytics, groupBy]
  );

  const currencyCode = analytics?.summary.currencyCode ?? null;

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <BarChart3 className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-900">
              Warehouse statistics
            </h2>
          </div>
          <button
            onClick={loadAnalytics}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      <div className="extranet-container-body">
        {(error || statsError) && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-red-800">{error || statsError}</span>
            </div>
          </div>
        )}

        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 items-end">
          <WarehouseMultiSelectFilter
            label="Hotel"
            placeholder="All hotels"
            options={hotelOptions}
            selectedValues={selectedHotelCodes}
            onChange={setSelectedHotelCodes}
          />
          <input
            type="date"
            value={filters.from}
            onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))}
            className="form-input !mt-0 !h-10 max-h-10 box-border py-1.5 text-sm leading-snug"
          />
          <input
            type="date"
            value={filters.to}
            onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))}
            className="form-input !mt-0 !h-10 max-h-10 box-border py-1.5 text-sm leading-snug"
          />
          <select
            value={groupBy}
            onChange={(e) =>
              handleGroupByChange(e.target.value as WarehouseAnalyticsGroupBy)
            }
            className="form-input !mt-0 !h-10 min-h-0 max-h-10 box-border py-1.5 text-sm leading-snug"
          >
            <option value="day">By day</option>
            <option value="month">By month</option>
          </select>
          <button
            onClick={loadAnalytics}
            className="flex h-10 min-h-10 max-h-10 shrink-0 items-center justify-center self-end rounded-md bg-indigo-600 px-4 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <Search className="h-4 w-4 mr-2" />
            Analyze
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">
            Loading statistics...
          </div>
        ) : analytics ? (
          <>
            {analytics.filters.truncated && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                Analysis limited to {formatNumber(analytics.filters.rowsAnalyzed)} reservations.
                Narrow the filters for a complete view.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
              <div className="bg-white p-4 border border-gray-200 rounded-md">
                <div className="text-sm text-gray-500">Reservations</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {formatNumber(analytics.summary.totalReservations)}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {formatNumber(analytics.summary.activeReservations)} active ·{" "}
                  {formatNumber(analytics.summary.cancelledReservations)} cancelled
                </div>
              </div>
              <div className="bg-white p-4 border border-gray-200 rounded-md">
                <div className="text-sm text-gray-500">Revenue</div>
                <div className="text-2xl font-semibold text-indigo-600">
                  {formatCurrency(analytics.summary.totalRevenue, currencyCode)}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Average basket{" "}
                  {formatCurrency(analytics.summary.averageRevenue, currencyCode)}
                </div>
              </div>
              <div className="bg-white p-4 border border-gray-200 rounded-md">
                <div className="text-sm text-gray-500">Nights & cancellations</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {formatNumber(analytics.summary.totalNights)}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {formatNumber(analytics.summary.averageNights, 1)} nights/reservation ·{" "}
                  {formatNumber(analytics.summary.cancellationRate, 1)}% cancellations
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
              <SimpleBarChart
                title={
                  groupBy === "month"
                    ? "Reservations by month"
                    : "Reservations by day"
                }
                data={reservationChartData}
                valueFormatter={(value) => formatNumber(value)}
                colorClass="bg-indigo-500"
              />
              <SimpleBarChart
                title={
                  groupBy === "month"
                    ? "Revenue by month"
                    : "Revenue by day"
                }
                data={revenueChartData}
                valueFormatter={(value) => formatCurrency(value, currencyCode)}
                colorClass="bg-emerald-500"
              />
            </div>

            <div className="mb-4 flex items-center text-sm text-gray-500">
              <TrendingUp className="h-4 w-4 mr-2" />
              Period analyzed:{" "}
              {new Date(analytics.filters.from).toLocaleDateString("en-US")} →{" "}
              {new Date(analytics.filters.to).toLocaleDateString("en-US")} ·{" "}
              {formatNumber(analytics.filters.rowsAnalyzed)} reservations analyzed
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
              <BreakdownTable
                title="By hotel"
                rows={analytics.byHotel ?? []}
                currencyCode={currencyCode}
                resolveLabel={getHotelDisplayName}
              />
              <BreakdownTable
                title="By status"
                rows={analytics.byStatus}
                currencyCode={currencyCode}
              />
              <BreakdownTable
                title="By PMS"
                rows={analytics.byPms}
                currencyCode={currencyCode}
              />
              <BreakdownTable
                title="By channel"
                rows={analytics.byChannel}
                currencyCode={currencyCode}
              />
            </div>

            {analytics.byPeriod.length > 0 && (
              <div className="mt-6 bg-white p-4 border border-gray-200 rounded-md overflow-x-auto">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Detail by period
                </h3>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="table-header">Period</th>
                      <th className="table-header">Reservations</th>
                      <th className="table-header">Cancelled</th>
                      <th className="table-header">Nights</th>
                      <th className="table-header">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {analytics.byPeriod.map((row) => (
                      <tr key={row.period}>
                        <td className="table-cell font-medium">
                          {formatPeriodLabel(row.period, analytics.filters.groupBy)}
                        </td>
                        <td className="table-cell">{formatNumber(row.reservations)}</td>
                        <td className="table-cell">{formatNumber(row.cancelled)}</td>
                        <td className="table-cell">{formatNumber(row.nights)}</td>
                        <td className="table-cell">
                          {formatCurrency(row.revenue, currencyCode)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
